import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  AXES,
  ACTIVE_CATALOG,
  ACTIVE_CITIES,
  CITY_LABEL,
  CITY_INTRO,
  GUIDES,
  CITY_TAG,
  HERO_CARDS,
  MAX_SWIPES,
  PERSONA_CARDS,
  SLOTS,
  SWIPE_SECONDS,
  UNI,
  buildPlanner,
  buildProfile,
  cityChips,
  cityEn,
  computeAxes,
  computeUni,
  emojiFor,
  TRIP_LENGTHS,
  fmtLeg,
  guideChips,
  hasFullGuide,
  legFor,
  OPP,
  monthOk,
  paletteFor,
  pickNext,
  slotOf,
  slotOk,
  quote,
} from "@lantern/core";
import type { ActiveCity, Experience } from "@lantern/core";
import ExperienceDetail from "./ExperienceDetail";
import { completeSession, openSession, reserve } from "./lib/telemetry";

// =====================================================================
// SIDEKICK — swipe your taste, get a trip, meet your local sidekick.
// This file is the UI shell only. All scoring, routing, matching and
// planning logic lives in @lantern/core, where it is unit tested.
// Media resolves at runtime from /public: /{id}.mp4 -> /{id}.jpg
// (Ken Burns) -> generated gradient. Drop a file in public/ and the
// card upgrades itself — no code edits.
// =====================================================================

// the "chart moment": a taste fingerprint rendered from the inferred profile.
// One accent color, neutral grid — built to be screenshotted.
const Hl = ({ text }) =>
  text.split("*").map((t, i) =>
    i % 2 ? (
      <em key={i} style={{ color: "#F5F2E9", fontFamily: "Georgia, serif" }}>
        {t}
      </em>
    ) : (
      t
    ),
  );

function Fingerprint({ axes, uni }) {
  const stations = [
    { l: "Food", v: uni.cul },
    { l: "Night", v: uni.nig },
    { l: "Thrill", v: uni.adv },
    { l: "Craft", v: uni.cra },
    { l: "Shop", v: uni.sho },
    ...AXES.map((a) => {
      const { lean, care } = axes[a.key];
      const pos = lean >= 0;
      return { l: pos ? a.labels[1] : a.labels[0], v: Math.abs(lean) * care };
    }),
  ];
  const C = 150,
    CY = 122,
    R = 84;
  const pt = (i, r) => {
    const ang = (Math.PI * 2 * i) / stations.length - Math.PI / 2;
    return [C + r * Math.cos(ang), CY + r * Math.sin(ang)];
  };
  const pts = stations.map((s, i) => pt(i, 16 + Math.max(0.07, s.v) * R));
  // closed catmull-rom -> smooth organic shape
  const n = pts.length;
  let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n],
      p1 = pts[i],
      p2 = pts[(i + 1) % n],
      p3 = pts[(i + 2) % n];
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C ${c1[0].toFixed(1)} ${c1[1].toFixed(1)}, ${c2[0].toFixed(1)} ${c2[1].toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  const top3 = [...stations.keys()].sort((a, b) => stations[b].v - stations[a].v).slice(0, 3);
  return (
    <svg viewBox="0 0 300 252" className="w-full">
      {[0.5, 1].map((k) => (
        <circle
          key={k}
          cx={C}
          cy={CY}
          r={16 + k * R}
          fill="none"
          stroke="#1E2236"
          strokeWidth="1"
        />
      ))}
      <path
        d={d}
        fill="rgba(228,87,46,.13)"
        stroke="#E4572E"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {pts.map(
        (p, i) => top3.includes(i) && <circle key={i} cx={p[0]} cy={p[1]} r="3" fill="#E4572E" />,
      )}
      {stations.map((st, i) => {
        const [x, y] = pt(i, 16 + R + 15);
        const hot = top3.includes(i);
        return (
          <text
            key={i}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={hot ? 11 : 9.5}
            fontWeight={hot ? 600 : 400}
            fill={hot ? "#F5F2E9" : "#5A5F78"}
          >
            {hot ? `${st.l} ${Math.round(st.v * 100)}` : st.l}
          </text>
        );
      })}
    </svg>
  );
}

// ---- day planner: half-day chunks with slot affinity ----

function CardMedia({ card, still = false, advanceRef = null }: any) {
  const mid = card.media || card.id;
  const [vid, setVid] = useState(true);
  const [imgs, setImgs] = useState(null); // null = probing, [] = none found
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setVid(true);
    setIdx(0);
    setImgs(null);
    let alive = true;
    const cands = [`/${mid}-1.jpg`, `/${mid}-2.jpg`, `/${mid}-3.jpg`, `/${mid}.jpg`];
    Promise.all(
      cands.map(
        (src) =>
          new Promise((res) => {
            const im = new Image();
            im.onload = () => res(src);
            im.onerror = () => res(null);
            im.src = src;
          }),
      ),
    ).then((rs) => {
      if (alive) setImgs(rs.filter(Boolean));
    });
    return () => {
      alive = false;
    };
  }, [card.id]);
  useEffect(() => {
    if (still || !imgs || imgs.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % imgs.length), 2400);
    return () => clearInterval(t);
  }, [imgs, still, card.id]);
  useEffect(() => {
    if (advanceRef)
      advanceRef.current = () =>
        setIdx((i) => (imgs && imgs.length > 1 ? (i + 1) % imgs.length : i));
  });
  const g = card.g || paletteFor(card);
  return (
    <>
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(165deg, ${g[0]}, ${g[1]} 55%, ${g[2]})`,
          animation: "kenburns 9s ease-in-out infinite alternate",
        }}
      />
      {imgs && imgs.length === 0 && !vid && (
        <div
          className="absolute top-0 left-0 right-0 flex justify-center pt-12"
          style={{ fontSize: 90, filter: "drop-shadow(0 12px 24px rgba(0,0,0,.4))" }}
        >
          {card.em || emojiFor(card)}
        </div>
      )}
      {imgs && imgs.length > 0 && (
        <img
          key={imgs[idx]}
          src={imgs[idx]}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ animation: "kenburns 12s ease-in-out infinite alternate" }}
        />
      )}
      {vid && (
        <video
          key={card.id}
          src={`/${mid}.mp4`}
          autoPlay
          muted
          loop
          playsInline
          onError={() => setVid(false)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div
        className="absolute inset-0"
        style={{
          background: "linear-gradient(to top, rgba(10,11,20,.88) 0%, rgba(10,11,20,0) 45%)",
        }}
      />
      {!still && imgs && imgs.length > 1 && (
        <div className="absolute top-2 left-4 right-4 flex gap-1" style={{ zIndex: 5 }}>
          {imgs.map((_, i) => (
            <div
              key={i}
              className="flex-1 rounded-full"
              style={{
                height: 3,
                background: i === idx ? "#F5F2E9" : "rgba(245,242,233,.28)",
                transition: "background .2s",
              }}
            />
          ))}
        </div>
      )}
    </>
  );
}

// ---------- app ----------

export default function Sidekick() {
  const now = new Date();
  const [phase, setPhase] = useState("intro"); // intro | swipe | quiz | results
  const [tripMonth, setTripMonth] = useState(now.getMonth() + 2 > 12 ? 1 : now.getMonth() + 2);
  const [kids, setKids] = useState(false);
  const [kidAge, setKidAge] = useState(5);
  const [settingsOpen, setSettingsOpen] = useState(false);
  /** Experience shown in the detail sheet, or null. */
  const [detailExp, setDetailExp] = useState<Experience | null>(null);
  const [tripDays, setTripDays] = useState<number>(7);
  // Set when the traveller has already booked flights — the city stops being
  // a recommendation and the deck narrows to it.
  const [lockedCity, setLockedCity] = useState<ActiveCity | null>(null);
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    if (phase !== "intro") return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_CARDS.length), 5000);
    return () => clearInterval(t);
  }, [phase]);
  const [likes, setLikes] = useState([]);
  const [dislikes, setDislikes] = useState([]);
  const [tally, setTally] = useState({ E: 0, I: 0, N: 0, S: 0, F: 0, T: 0, J: 0, P: 0 });
  const [email, setEmail] = useState("");
  const [planner, setPlanner] = useState(null);
  const [, setSel] = useState<any>(null); // { c, d, s } selected chunk
  const [cityTab, setCityTab] = useState(0);
  const [, setWarn] = useState("");
  const [pinned, setPinned] = useState(new Set<string>());
  const [dnd, setDnd] = useState(null); // { d, s, x, y, over, valid } while dragging
  const [bookingFrom, setBookingFrom] = useState("plan"); // booking has two entry points — go back to the right one
  const [voicePlaying, setVoicePlaying] = useState(false); // fake voice-note player on the profile
  const [photoIdx, setPhotoIdx] = useState(0);
  const dndStart = useRef<any>(null);
  const slotRefs = useRef<Record<string, HTMLElement | null>>({});
  const plannerRef = useRef<any>(null);
  const pinnedRef = useRef(pinned);
  plannerRef.current = planner;
  pinnedRef.current = pinned;
  const [log, setLog] = useState([]);
  const shownAt = useRef(Date.now());
  const [card, setCard] = useState(null);
  const [nextCard, setNextCard] = useState(null);
  const [timeLeft, setTimeLeft] = useState(SWIPE_SECONDS);
  const [drag, setDrag] = useState({ x: 0, y: 0, active: false });
  const seenRef = useRef(new Set<string>());
  const expRef = useRef({});
  const personaQ = useRef([]);
  const served = useRef(0);
  const startPos = useRef<any>(null);
  const mediaAdvance = useRef<any>(null);
  const timerRef = useRef<any>(null);
  const [openGuide, setOpenGuide] = useState<string | null>(null);
  const sessionId = useRef<string | null>(null);
  const completed = useRef(false);
  const history = useRef([]); // snapshot stack so a swipe can be taken back

  const pool = useMemo(
    () =>
      ACTIVE_CATALOG.filter((c) => monthOk(c.months, tripMonth))
        .filter((c) => !kids || c.family >= 0.15)
        // Already booked: only show cards for the city they are going to.
        .filter((c) => !lockedCity || c.city === lockedCity),
    [tripMonth, kids, lockedCity],
  );

  const axes = useMemo(() => computeAxes(likes, dislikes), [likes, dislikes]);
  const uni = useMemo(() => computeUni(likes, dislikes), [likes, dislikes]);
  const swipes = likes.length + dislikes.length;

  const draw = () => {
    served.current += 1;
    if (served.current % 3 === 0 && personaQ.current.length) return personaQ.current.shift();
    const c = pickNext(pool, seenRef.current, expRef.current);
    if (c) {
      seenRef.current.add(c.id);
      return c;
    }
    // Experience pool is dry. With a locked city there may be as few as nine
    // cards, well under MAX_SWIPES, so spend any remaining persona cards
    // rather than dealing blanks.
    return personaQ.current.length ? personaQ.current.shift() : null;
  };

  const begin = () => {
    seenRef.current = new Set();
    expRef.current = {};
    served.current = 0;
    history.current = [];
    personaQ.current = [...PERSONA_CARDS].sort(() => Math.random() - 0.5);
    const first = draw();
    const second = draw();
    setCard(first);
    setNextCard(second);
    setLikes([]);
    setDislikes([]);
    setTally({ E: 0, I: 0, N: 0, S: 0, F: 0, T: 0, J: 0, P: 0 });
    setLog([]);
    shownAt.current = Date.now();
    setTimeLeft(SWIPE_SECONDS);
    completed.current = false;
    void openSession({ tripMonth, tripDays, kids }).then((id) => {
      sessionId.current = id;
    });
    setPhase("swipe");
  };

  useEffect(() => {
    // Reading the detail sheet should not burn swipe time.
    if (phase !== "swipe" || detailExp) return;
    timerRef.current = setInterval(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, detailExp]);
  useEffect(() => {
    // `!card` matters now that a locked city can leave fewer cards than
    // MAX_SWIPES — without it the deck stalls on a blank until the clock runs out.
    if (phase === "swipe" && (timeLeft <= 0 || swipes >= MAX_SWIPES || !card)) {
      clearInterval(timerRef.current);
      setPhase("results");
    }
  }, [timeLeft, swipes, phase, card]);

  const commit = (dir) => {
    if (!card) return;
    // snapshot everything a swipe touches — refs are mutated in place, so copy them
    history.current.push({
      likes,
      dislikes,
      tally,
      log,
      card,
      nextCard,
      exp: { ...expRef.current },
      seen: new Set(seenRef.current),
      served: served.current,
      personaQ: [...personaQ.current],
    });
    AXES.forEach(({ key }) => {
      const v = card.ax[key];
      const pole = key + (v > 0 ? "+" : "-");
      expRef.current[pole] = (expRef.current[pole] || 0) + Math.abs(v);
    });
    UNI.forEach(({ key }) => {
      expRef.current[key] = (expRef.current[key] || 0) + card.u[key];
    });
    const ms = Date.now() - shownAt.current;
    shownAt.current = Date.now();
    setLog((g) => [...g, { card, dir, ms }]);
    if (card.type === "persona") {
      setTally((t) => {
        const n = { ...t };
        if (dir === "right") n[card.letter] += 1;
        else n[OPP[card.letter]] += 0.6;
        return n;
      });
    } else {
      if (dir === "right") setLikes((l) => [...l, card]);
      else setDislikes((l) => [...l, card]);
    }
    const upcoming = draw();
    setCard(nextCard);
    setNextCard(upcoming);
    setDrag({ x: 0, y: 0, active: false });
  };

  const undo = () => {
    const prev = history.current.pop();
    if (!prev) return;
    expRef.current = prev.exp;
    seenRef.current = prev.seen;
    served.current = prev.served;
    personaQ.current = prev.personaQ;
    setLikes(prev.likes);
    setDislikes(prev.dislikes);
    setTally(prev.tally);
    setLog(prev.log);
    setCard(prev.card);
    setNextCard(prev.nextCard);
    shownAt.current = Date.now();
    setDrag({ x: 0, y: 0, active: false });
  };

  const onDown = (e) => {
    const p = e.touches ? e.touches[0] : e;
    startPos.current = { x: p.clientX, y: p.clientY };
    setDrag((d) => ({ ...d, active: true }));
  };
  const onMove = (e) => {
    if (!drag.active || !startPos.current) return;
    const p = e.touches ? e.touches[0] : e;
    setDrag({
      x: p.clientX - startPos.current.x,
      y: (p.clientY - startPos.current.y) * 0.3,
      active: true,
    });
  };
  const onUp = () => {
    if (!drag.active) return;
    if (drag.x > 90) commit("right");
    else if (drag.x < -90) commit("left");
    else {
      if (Math.abs(drag.x) < 8 && Math.abs(drag.y) < 8) mediaAdvance.current?.(); // tap = next photo
      setDrag({ x: 0, y: 0, active: false });
    }
    startPos.current = null;
  };
  useEffect(() => {
    const key = (e) => {
      if (phase === "swipe" && !detailExp) {
        if (e.key === "ArrowRight") commit("right");
        if (e.key === "ArrowLeft") commit("left");
        if (e.key === "ArrowUp" || e.key === "Backspace") {
          e.preventDefault();
          undo();
        }
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });

  const results = useMemo(() => {
    if (!["results", "match", "profile", "plan", "booking", "booked", "planner"].includes(phase))
      return null;
    return buildProfile({
      tally,
      uni,
      axes,
      kids,
      likes,
      tripMonth,
      tripDays,
      log,
      constraints: { kids, youngestAge: kidAge },
      lockedCity: lockedCity ?? undefined,
    });
  }, [phase, tally, uni, axes, kids, kidAge, likes, tripMonth, tripDays, log, lockedCity]);

  // Record the finished run exactly once, when results first appear.
  useEffect(() => {
    if (!results || completed.current) return;
    completed.current = true;
    void completeSession(sessionId.current, log, results, tally, axes, uni);
  }, [results, log, tally, axes, uni]);

  const tripSummary = [
    `${tripDays} days`,
    new Date(2000, tripMonth - 1, 1).toLocaleString("en", { month: "short" }),
    kids ? `kids ${kidAge}+` : "no kids",
    lockedCity ? CITY_LABEL[lockedCity].en : "pick my city",
  ].join(" \u00b7 ");

  const rot = drag.x / 18;
  const likeOp = Math.min(1, Math.max(0, drag.x / 80));
  const nopeOp = Math.min(1, Math.max(0, -drag.x / 80));

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center p-4"
      style={{
        background:
          "radial-gradient(1200px 800px at 70% -10%, #232A45 0%, #14172A 45%, #0D0F1C 100%)",
        fontFamily: "'Avenir Next', 'Segoe UI', system-ui, sans-serif",
        color: "#F5F2E9",
      }}
    >
      <style>{`
        @keyframes kenburns { 0% { transform: scale(1.02) translate(0,0); } 100% { transform: scale(1.14) translate(-2%, -2%); } }
        @keyframes floatUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes lanternGlow { 0%,100% { opacity:.5 } 50% { opacity:.9 } }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important; } }
        .fadeup { animation: floatUp .5s ease both; }
        .noscroll::-webkit-scrollbar { display: none; }
      `}</style>

      <div className="flex flex-col lg:flex-row gap-6 items-center lg:items-stretch w-full max-w-5xl">
        {/* PHONE */}
        <div
          className="relative rounded-[2.4rem] p-3 shadow-2xl"
          style={{
            width: 360,
            height: 700,
            background: "#1B1E30",
            border: "1px solid #2E3350",
            flexShrink: 0,
          }}
        >
          <div
            className="w-full h-full rounded-[1.9rem] overflow-hidden relative flex flex-col"
            style={{ background: "#12141F" }}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-2 z-20">
              <div className="flex items-center gap-2">
                <span style={{ fontSize: 20 }}>🤙</span>
                <span
                  className="tracking-widest text-sm"
                  style={{ fontWeight: 700, letterSpacing: "0.25em" }}
                >
                  SIDEKICK
                </span>
              </div>
              {phase === "swipe" && (
                <div
                  className="text-xs px-2 py-1 rounded-full"
                  style={{
                    background: timeLeft <= 10 ? "#E4572E" : "#242842",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {Math.floor(Math.max(0, timeLeft) / 60)}:
                  {String(Math.max(0, timeLeft) % 60).padStart(2, "0")}
                </div>
              )}
            </div>

            {/* INTRO — full-bleed rotating hero */}
            {phase === "intro" && (
              <div className="flex-1 flex flex-col justify-end relative overflow-hidden">
                {/* stacked backdrops, crossfading */}
                {HERO_CARDS.map((h, i) => (
                  <div
                    key={h.id}
                    className="absolute inset-0"
                    style={{ opacity: i === heroIdx ? 1 : 0, transition: "opacity 1.4s ease" }}
                  >
                    <CardMedia card={h} still />
                  </div>
                ))}
                <div className="absolute inset-0" style={{ background: "rgba(10,11,20,.30)" }} />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(10,11,20,.98) 0%, rgba(10,11,20,.82) 32%, rgba(10,11,20,.35) 62%, rgba(10,11,20,.12) 100%)",
                  }}
                />

                <div className="relative p-6 pb-7">
                  {/* live location chip */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#E4572E" }} />
                    <span
                      key={heroIdx}
                      className="text-xs uppercase fadeup"
                      style={{ color: "#CFCBBF", letterSpacing: ".18em" }}
                    >
                      {HERO_CARDS[heroIdx]?.label} · right now
                    </span>
                  </div>

                  <h1
                    style={{
                      color: "#F5F2E9",
                      fontFamily: "Georgia, 'Iowan Old Style', serif",
                      fontSize: 40,
                      lineHeight: 1.04,
                      fontWeight: 400,
                      letterSpacing: "-0.01em",
                      textShadow: "0 2px 18px rgba(10,11,20,.85)",
                    }}
                  >
                    Travel China with
                    <br />
                    <em style={{ color: "#E4572E" }}>a local who gets you.</em>
                  </h1>
                  {!settingsOpen && (
                    <p
                      className="mt-2 text-sm"
                      style={{
                        color: "#CFCBBF",
                        lineHeight: 1.5,
                        textShadow: "0 1px 10px rgba(10,11,20,.9)",
                      }}
                    >
                      60 seconds of swiping finds your person — and builds the trip you'll take
                      together.
                    </p>
                  )}

                  <button
                    onClick={begin}
                    className="mt-5 w-full py-3.5 rounded-full text-sm font-semibold tracking-wide"
                    style={{ background: "#E4572E", color: "#FFF7F0" }}
                  >
                    Start swiping →
                  </button>

                  {/* Everything else collapses into one line. Six controls on the
                      landing screen buried the one action that matters. */}
                  <button
                    onClick={() => setSettingsOpen((v) => !v)}
                    className="mt-2.5 w-full flex items-center justify-center gap-1.5 py-1.5"
                    style={{ fontSize: 11, color: "#8A8FA8", letterSpacing: ".02em" }}
                  >
                    <span>{tripSummary}</span>
                    <span style={{ fontSize: 9, opacity: 0.8 }}>{settingsOpen ? "▲" : "▼"}</span>
                  </button>

                  {settingsOpen && (
                    <div className="fadeup">
                      <div className="flex items-center gap-2 mt-5">
                        {TRIP_LENGTHS.map((d) => (
                          <button
                            key={d}
                            onClick={() => setTripDays(d)}
                            className="py-1.5 px-3 rounded-full text-xs"
                            style={{
                              background:
                                tripDays === d ? "rgba(228,87,46,.14)" : "rgba(18,20,31,.5)",
                              backdropFilter: "blur(12px)",
                              border: `1px solid ${tripDays === d ? "#E4572E" : "rgba(245,242,233,.18)"}`,
                              color: tripDays === d ? "#E4572E" : "#CFCBBF",
                              letterSpacing: ".04em",
                            }}
                          >
                            {d} days
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="relative">
                          <select
                            value={tripMonth}
                            onChange={(e) => setTripMonth(Number(e.target.value))}
                            className="py-1.5 pl-3 pr-7 rounded-full text-xs cursor-pointer"
                            style={{
                              appearance: "none",
                              WebkitAppearance: "none",
                              MozAppearance: "none",
                              background: "rgba(18,20,31,.5)",
                              backdropFilter: "blur(12px)",
                              border: "1px solid rgba(245,242,233,.18)",
                              color: "#CFCBBF",
                              letterSpacing: ".04em",
                            }}
                          >
                            {[
                              "January",
                              "February",
                              "March",
                              "April",
                              "May",
                              "June",
                              "July",
                              "August",
                              "September",
                              "October",
                              "November",
                              "December",
                            ].map((m, i) => (
                              <option key={m} value={i + 1}>
                                {m}
                              </option>
                            ))}
                          </select>
                          <span
                            className="absolute pointer-events-none"
                            style={{
                              right: 10,
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: 9,
                              color: "#8A8FA8",
                            }}
                          >
                            ▾
                          </span>
                        </div>
                        <button
                          onClick={() => setKids((k) => !k)}
                          className="py-1.5 px-3 rounded-full text-xs"
                          style={{
                            background: kids ? "rgba(228,87,46,.14)" : "rgba(18,20,31,.5)",
                            backdropFilter: "blur(12px)",
                            border: `1px solid ${kids ? "#E4572E" : "rgba(245,242,233,.18)"}`,
                            color: kids ? "#E4572E" : "#CFCBBF",
                            letterSpacing: ".04em",
                          }}
                        >
                          Kids · {kids ? "on" : "off"}
                        </button>
                      </div>
                      {/* Youngest traveller. Drives the altitude and age guardrails,
                        so a 14-year-old is not planned for like a toddler. */}
                      {kids && (
                        <div className="flex items-center justify-center gap-1.5 mt-2">
                          <span style={{ fontSize: 10, color: "#8A8FA8", letterSpacing: ".04em" }}>
                            Youngest · 最小年龄
                          </span>
                          {[3, 5, 8, 12, 15].map((a) => (
                            <button
                              key={a}
                              onClick={() => setKidAge(a)}
                              className="rounded-full"
                              style={{
                                fontSize: 11,
                                padding: "2px 9px",
                                lineHeight: 1.6,
                                background:
                                  kidAge === a ? "rgba(228,87,46,.16)" : "rgba(18,20,31,.5)",
                                border: `1px solid ${kidAge === a ? "#E4572E" : "rgba(245,242,233,.18)"}`,
                                color: kidAge === a ? "#E4572E" : "#CFCBBF",
                              }}
                            >
                              {a}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Already booked? Then the city is a fact, not a suggestion.
                        The deck still runs — it is what matches the sidekick and
                        orders the days — but only shows cards for that city. */}
                      <div className="mt-3">
                        <div
                          className="text-center"
                          style={{ fontSize: 10, color: "#8A8FA8", letterSpacing: ".06em" }}
                        >
                          Already booked flights? · 已订好机票？
                        </div>
                        <div className="flex items-center justify-center gap-1.5 mt-1.5 flex-wrap">
                          <button
                            onClick={() => setLockedCity(null)}
                            className="rounded-full"
                            style={{
                              fontSize: 11,
                              padding: "3px 10px",
                              lineHeight: 1.6,
                              background: !lockedCity ? "rgba(228,87,46,.16)" : "rgba(18,20,31,.5)",
                              border: `1px solid ${!lockedCity ? "#E4572E" : "rgba(245,242,233,.18)"}`,
                              color: !lockedCity ? "#E4572E" : "#CFCBBF",
                            }}
                          >
                            Pick for me
                          </button>
                          {ACTIVE_CITIES.map((c) => (
                            <button
                              key={c}
                              onClick={() => setLockedCity(lockedCity === c ? null : c)}
                              className="rounded-full"
                              style={{
                                fontSize: 11,
                                padding: "3px 10px",
                                lineHeight: 1.6,
                                background:
                                  lockedCity === c ? "rgba(228,87,46,.16)" : "rgba(18,20,31,.5)",
                                border: `1px solid ${lockedCity === c ? "#E4572E" : "rgba(245,242,233,.18)"}`,
                                color: lockedCity === c ? "#E4572E" : "#CFCBBF",
                              }}
                            >
                              {CITY_LABEL[c].en}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mt-3 text-center" style={{ fontSize: 10, color: "#6E7290" }}>
                    Photos & footage: Pexels · Pixabay
                  </div>
                </div>
              </div>
            )}

            {/* SWIPE */}
            {phase === "swipe" && (
              <div className="flex-1 relative px-4 pb-4 pt-1 flex flex-col">
                <div className="relative flex-1" style={{ touchAction: "none" }}>
                  {nextCard && (
                    <div
                      className="absolute inset-0 rounded-2xl"
                      style={{
                        background: `linear-gradient(165deg, ${paletteFor(nextCard)[0]}, ${paletteFor(nextCard)[1]} 55%, ${paletteFor(nextCard)[2]})`,
                        transform: "scale(.95) translateY(10px)",
                        opacity: 0.6,
                      }}
                    />
                  )}
                  {card && (
                    <div
                      onPointerDown={onDown}
                      onPointerMove={onMove}
                      onPointerUp={onUp}
                      onPointerLeave={onUp}
                      className="absolute inset-0 rounded-2xl overflow-hidden cursor-grab select-none shadow-xl"
                      style={{
                        transform: `translate(${drag.x}px, ${drag.y}px) rotate(${rot}deg)`,
                        transition: drag.active ? "none" : "transform .25s ease",
                      }}
                    >
                      <CardMedia card={card} advanceRef={mediaAdvance} />
                      <div
                        className="absolute top-5 left-5 px-3 py-1 rounded-lg border-2 text-lg font-bold"
                        style={{
                          borderColor: "#E4572E",
                          color: "#E4572E",
                          transform: "rotate(-14deg)",
                          opacity: likeOp,
                        }}
                      >
                        {card.type === "persona" ? "FACTS" : "TAKE ME"}
                      </div>
                      <div
                        className="absolute top-5 right-5 px-3 py-1 rounded-lg border-2 text-lg font-bold"
                        style={{
                          borderColor: "#8A8FA8",
                          color: "#B8B5AC",
                          transform: "rotate(14deg)",
                          opacity: nopeOp,
                        }}
                      >
                        {card.type === "persona" ? "NOPE" : "PASS"}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <div
                          className="text-xs uppercase tracking-widest mb-1"
                          style={{ color: card.type === "persona" ? "#E4572E" : "#8A8FA8" }}
                        >
                          {card.type === "persona"
                            ? "Hot take 🔥"
                            : `${card.city} · ${card.region}`}
                        </div>
                        <div
                          style={{
                            color: "#F5F2E9",
                            fontFamily: "Georgia, serif",
                            fontSize: 22,
                            lineHeight: 1.15,
                          }}
                        >
                          {card.en}
                        </div>
                        {card.cn && (
                          <div className="text-sm mt-0.5" style={{ color: "#CFCBBF" }}>
                            {card.cn}
                          </div>
                        )}
                        {/* Reality chips, plus the way into the full page. The card
                            itself cannot open it — tap advances the photo and drag
                            swipes — so detail needs its own button. */}
                        {card.type !== "persona" && (
                          <button
                            onPointerDown={(ev) => ev.stopPropagation()}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setDetailExp(card);
                            }}
                            className="inline-flex items-center gap-1 rounded-full mt-2"
                            style={{
                              fontSize: 11,
                              lineHeight: 1.7,
                              padding: "3px 11px",
                              color: "#FFF7F0",
                              background: "rgba(228,87,46,.92)",
                              border: "1px solid rgba(255,247,240,.25)",
                            }}
                          >
                            More about this ›
                          </button>
                        )}
                        {card.type !== "persona" &&
                          (() => {
                            const chips = guideChips(GUIDES[card.id]);
                            if (!chips.length) return null;
                            return (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {chips.map((ch) => (
                                  <span
                                    key={ch.icon + ch.label}
                                    className="inline-flex items-center gap-1 rounded-full"
                                    style={{
                                      fontSize: 10.5,
                                      // emoji need real line-height or they clip
                                      lineHeight: 1.6,
                                      padding: "2px 8px",
                                      letterSpacing: ".02em",
                                      color: ch.warn ? "#FFD9CC" : "#CFCBBF",
                                      background: ch.warn
                                        ? "rgba(228,87,46,.22)"
                                        : "rgba(10,11,20,.55)",
                                      border: `1px solid ${ch.warn ? "rgba(228,87,46,.55)" : "rgba(207,203,191,.22)"}`,
                                      backdropFilter: "blur(2px)",
                                    }}
                                  >
                                    <span style={{ fontSize: 11, lineHeight: 1.6 }}>{ch.icon}</span>
                                    {ch.label}
                                  </span>
                                ))}
                              </div>
                            );
                          })()}
                      </div>
                    </div>
                  )}
                </div>
                <div className="pt-4">
                  <div className="flex items-center justify-center gap-8">
                    <button
                      onClick={() => commit("left")}
                      className="w-14 h-14 rounded-full text-xl"
                      style={{ background: "#242842", border: "1px solid #3A3F63" }}
                    >
                      ✕
                    </button>
                    <button
                      onClick={undo}
                      disabled={!history.current.length}
                      title="Take back the last swipe"
                      className="w-10 h-10 rounded-full text-sm"
                      style={{
                        background: "transparent",
                        border: "1px solid #2E3350",
                        color: "#8A8FA8",
                        opacity: history.current.length ? 1 : 0.3,
                        cursor: history.current.length ? "pointer" : "default",
                      }}
                    >
                      ↩
                    </button>
                    <button
                      onClick={() => commit("right")}
                      className="w-14 h-14 rounded-full text-xl"
                      style={{ background: "#E4572E" }}
                    >
                      ♥
                    </button>
                  </div>
                  <div
                    className="text-center pt-2"
                    style={{
                      fontSize: 10,
                      color: "#6E7290",
                      fontVariantNumeric: "tabular-nums",
                      letterSpacing: ".06em",
                    }}
                  >
                    {swipes}/{MAX_SWIPES}
                  </div>
                </div>
              </div>
            )}

            {/* BOOKING — price-truth fake door: real quote, capture before charge */}
            {phase === "booking" &&
              results &&
              (() => {
                const days = results.per;
                // Families get a private car; three tier-one cities are large
                // and metro transfers with a small child are the part people
                // remember badly.
                const q = quote(days, { withCar: kids });
                const { total, deposit: DEPOSIT } = q;
                const sk = results.sidekick;
                return (
                  <div className="flex-1 overflow-y-auto px-5 pb-5 pt-2">
                    <button
                      onClick={() => setPhase(bookingFrom)}
                      className="text-xs"
                      style={{ color: "#6E7290" }}
                    >
                      ← Back
                    </button>
                    <div className="fadeup mt-2 flex items-center gap-3">
                      <div
                        className="flex items-center justify-center rounded-full flex-shrink-0"
                        style={{
                          width: 48,
                          height: 48,
                          background: "#171A2B",
                          border: "1px solid #2E3350",
                          fontSize: 26,
                        }}
                      >
                        {sk.emoji}
                      </div>
                      <div>
                        <div
                          style={{
                            color: "#F5F2E9",
                            fontFamily: "Georgia, serif",
                            fontSize: 22,
                            lineHeight: 1.15,
                          }}
                        >
                          {sk.name}
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: "#8A8FA8" }}>
                          {sk.base} · {results.route.name} · {days} days
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div
                        className="flex justify-between py-2.5 text-sm"
                        style={{ color: "#B8B5AC", borderTop: "1px solid #1E2236" }}
                      >
                        <span>
                          Sidekick · ${q.dayRate}/day × {days} days
                        </span>
                        <span style={{ color: "#F5F2E9", fontVariantNumeric: "tabular-nums" }}>
                          ${q.sidekickTotal.toLocaleString()}
                        </span>
                      </div>
                      {q.withCar && (
                        <div
                          className="flex justify-between py-2.5 text-sm"
                          style={{ color: "#B8B5AC", borderTop: "1px solid #1E2236" }}
                        >
                          <span>
                            Private car &amp; driver · ${q.carDayRate}/day × {days} days
                            <div style={{ fontSize: 10, color: "#6E7290", marginTop: 2 }}>
                              Added for families · 带孩子出行默认包车
                            </div>
                          </span>
                          <span style={{ color: "#F5F2E9", fontVariantNumeric: "tabular-nums" }}>
                            ${q.carTotal.toLocaleString()}
                          </span>
                        </div>
                      )}
                      <div
                        className="flex justify-between py-2.5 text-sm"
                        style={{ borderTop: "1px solid #1E2236" }}
                      >
                        <span style={{ color: "#F5F2E9", fontWeight: 600 }}>Total</span>
                        <span
                          style={{
                            color: "#F5F2E9",
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          ${total.toLocaleString()}
                        </span>
                      </div>
                      <div
                        className="flex justify-between py-2.5 text-sm"
                        style={{ color: "#B8B5AC", borderTop: "1px solid #1E2236" }}
                      >
                        <span>Trip plan & matching</span>
                        <span style={{ color: "#F5F2E9" }}>Free</span>
                      </div>
                      <div
                        className="flex justify-between py-2.5 text-sm"
                        style={{ borderTop: "1px solid #1E2236" }}
                      >
                        <span style={{ color: "#F5F2E9" }}>Due today · refundable deposit</span>
                        <span
                          style={{
                            color: "#F5F2E9",
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          ${DEPOSIT}
                        </span>
                      </div>
                      <div className="text-xs pb-1" style={{ color: "#6E7290", lineHeight: 1.5 }}>
                        Balance is only charged after you and {sk.name.split(" ")[0]} confirm the
                        plan together on a call.
                      </div>
                    </div>
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      type="email"
                      className="mt-3 w-full py-3 px-4 rounded-full text-sm"
                      style={{
                        background: "#171A2B",
                        border: "1px solid #3A3F63",
                        color: "#F5F2E9",
                      }}
                    />
                    <button
                      onClick={() => {
                        if (!email.includes("@")) return;
                        void reserve({
                          sessionId: sessionId.current,
                          email,
                          sidekick: sk.name,
                          route: results.route.name,
                          days,
                          quote: total,
                          deposit: DEPOSIT,
                        });
                        setPhase("booked");
                      }}
                      className="mt-3 w-full py-3 rounded-full text-sm font-semibold"
                      style={{
                        background: email.includes("@") ? "#E4572E" : "#3A3F63",
                        color: "#FFF7F0",
                        transition: "background .2s",
                      }}
                    >
                      Reserve · ${DEPOSIT} deposit →
                    </button>
                    <div
                      className="mt-3 text-center"
                      style={{ fontSize: 10, color: "#6E7290", lineHeight: 1.6 }}
                    >
                      Early access — nothing is charged today.
                      <br />
                      We will email you to confirm before any payment.
                    </div>
                  </div>
                );
              })()}

            {/* BOOKED — confirmation */}
            {phase === "booked" && results && (
              <div className="flex-1 flex flex-col justify-center px-6 pb-10 text-center">
                <div className="fadeup">
                  <div style={{ fontSize: 56 }}>{results.sidekick.emoji}</div>
                  <h2
                    style={{
                      color: "#F5F2E9",
                      fontFamily: "Georgia, serif",
                      fontSize: 22,
                      lineHeight: 1.2,
                      marginTop: 12,
                    }}
                  >
                    {results.sidekick.name.split(" ")[0]} has your trip.
                  </h2>
                  <p className="mt-2 text-sm" style={{ color: "#B8B5AC", lineHeight: 1.6 }}>
                    We'll email <span style={{ color: "#F5F2E9" }}>{email}</span> within 24 hours to
                    set up your intro call with {results.sidekick.name.split(" ")[0]}. Nothing has
                    been charged.
                  </p>
                  <button
                    onClick={() => {
                      setPlanner(
                        buildPlanner(results, uni, axes, tripMonth, kids, {
                          jitter: 0.4,
                          constraints: { kids, youngestAge: kidAge },
                        }),
                      );
                      setPinned(new Set<string>());
                      setCityTab(0);
                      setSel(null);
                      setWarn("");
                      setPhase("planner");
                    }}
                    className="mt-6 w-full py-3 rounded-full text-sm font-semibold"
                    style={{ background: "#E4572E", color: "#FFF7F0" }}
                  >
                    ✨ Surprise me — just handle it
                  </button>
                  <button
                    onClick={() => {
                      setPlanner(
                        buildPlanner(results, uni, axes, tripMonth, kids, {
                          constraints: { kids, youngestAge: kidAge },
                        }),
                      );
                      setPinned(new Set<string>());
                      setCityTab(0);
                      setSel(null);
                      setWarn("");
                      setPhase("planner");
                    }}
                    className="mt-2 w-full py-3 rounded-full text-sm font-semibold"
                    style={{ background: "#242842", border: "1px solid #3A3F63", color: "#F5F2E9" }}
                  >
                    Plan the days myself →
                  </button>
                  <button
                    onClick={() => setPhase("intro")}
                    className="mt-2 py-2 text-xs"
                    style={{ color: "#6E7290" }}
                  >
                    Done for now
                  </button>
                </div>
              </div>
            )}

            {/* PLANNER — drag chunks to move them; tap to pin; shuffle rerolls the unpinned */}
            {phase === "planner" &&
              results &&
              planner &&
              (() => {
                const cityP = planner[cityTab];
                const pinInfo = [];
                planner.forEach((cp) =>
                  cp.grid.forEach((day) =>
                    day.forEach((e) => {
                      if (e && pinned.has(e.id)) pinInfo.push({ city: cp.city, e });
                    }),
                  ),
                );
                const togglePin = (e) => {
                  if (!e || e.locked) return;
                  const p = new Set(pinned);
                  if (p.has(e.id)) p.delete(e.id);
                  else p.add(e.id);
                  setPinned(p);
                };
                const reshuffle = () => {
                  setPlanner(
                    buildPlanner(results, uni, axes, tripMonth, kids, {
                      jitter: 1.4,
                      prev: planner,
                      pinned,
                      constraints: { kids, youngestAge: kidAge },
                    }),
                  );
                };
                const hit = (x, y) => {
                  for (const [k, el] of Object.entries(slotRefs.current)) {
                    if (!el) continue;
                    const r = el.getBoundingClientRect();
                    if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
                      const [d, sl] = k.split("-").map(Number);
                      return { d, sl };
                    }
                  }
                  return null;
                };
                const canDrop = (src, tgt) => {
                  const grid = plannerRef.current[cityTab].grid;
                  const a = grid[src.d][src.sl],
                    b = grid[tgt.d][tgt.sl];
                  if (!a || a.locked) return false;
                  if (b && b.locked) return false;
                  if (tgt.d === src.d && tgt.sl === src.sl) return false;
                  if (b && pinnedRef.current.has(b.id)) return false;
                  if (!slotOk(a, tgt.sl)) return false;
                  if (b && !slotOk(b, src.sl)) return false;
                  return true;
                };
                const chunkDown = (ev, d, sl) => {
                  const cell = cityP.grid[d][sl];
                  if (!cell || cell.locked) return;
                  const start = {
                    d,
                    sl,
                    x0: ev.clientX,
                    y0: ev.clientY,
                    dragging: false,
                    locked: pinned.has(cell.id),
                  };
                  dndStart.current = start;
                  const move = (me) => {
                    const st = dndStart.current;
                    if (!st) return;
                    const dist = Math.hypot(me.clientX - st.x0, me.clientY - st.y0);
                    if (!st.dragging && dist > 8) {
                      if (st.locked) return;
                      st.dragging = true;
                    }
                    if (st.dragging) {
                      const over = hit(me.clientX, me.clientY);
                      setDnd({
                        d: st.d,
                        s: st.sl,
                        x: me.clientX,
                        y: me.clientY,
                        over,
                        valid: over ? canDrop({ d: st.d, sl: st.sl }, over) : false,
                      });
                    }
                  };
                  const up = (ue) => {
                    window.removeEventListener("pointermove", move);
                    window.removeEventListener("pointerup", up);
                    const st = dndStart.current;
                    dndStart.current = null;
                    if (!st) {
                      setDnd(null);
                      return;
                    }
                    if (!st.dragging) {
                      togglePin(cell);
                      setDnd(null);
                      return;
                    }
                    const over = hit(ue.clientX, ue.clientY);
                    if (over && canDrop({ d: st.d, sl: st.sl }, over)) {
                      setPlanner((prev) => {
                        const g = prev.map((cp) => ({ ...cp, grid: cp.grid.map((r) => [...r]) }));
                        const a = g[cityTab].grid[st.d][st.sl];
                        const b = g[cityTab].grid[over.d][over.sl];
                        g[cityTab].grid[st.d][st.sl] = b || null;
                        g[cityTab].grid[over.d][over.sl] = a;
                        return g;
                      });
                    }
                    setDnd(null);
                  };
                  window.addEventListener("pointermove", move);
                  window.addEventListener("pointerup", up);
                };
                const dragged = dnd ? cityP.grid[dnd.d]?.[dnd.s] : null;
                return (
                  <div className="flex-1 overflow-y-auto px-5 pb-5 pt-2">
                    <div className="fadeup">
                      <div className="text-xs" style={{ color: "#6E7290", letterSpacing: ".08em" }}>
                        Drag to move · tap to 📌 pin · › for detail
                      </div>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {planner.map((cp, i) => {
                          const nPins = cp.grid.flat().filter((e) => e && pinned.has(e.id)).length;
                          return (
                            <button
                              key={cp.city}
                              onClick={() => {
                                setCityTab(i);
                                slotRefs.current = {};
                              }}
                              className="text-xs px-3 py-1 rounded-full"
                              style={{
                                color: i === cityTab ? "#E4572E" : "#8A8FA8",
                                border: `1px solid ${i === cityTab ? "#E4572E" : "#2E3350"}`,
                                fontWeight: i === cityTab ? 600 : 400,
                              }}
                            >
                              {cityEn(cp.city)}
                              {nPins ? ` 📌${nPins}` : ""}
                            </button>
                          );
                        })}
                      </div>
                      {CITY_TAG[cityP.city] && (
                        <div
                          className="mt-2"
                          style={{
                            fontFamily: "Georgia, serif",
                            fontStyle: "italic",
                            fontSize: 14,
                            color: "#CFCBBF",
                            lineHeight: 1.45,
                          }}
                        >
                          “{CITY_TAG[cityP.city]}”
                        </div>
                      )}
                      {cityChips(cityP.city).length > 0 && (
                        <div className="flex gap-1.5 mt-1.5 flex-wrap">
                          {cityChips(cityP.city).map((ch) => (
                            <span
                              key={ch}
                              className="px-2 py-0.5 rounded-full"
                              style={{
                                fontSize: 10,
                                color: "#8A8FA8",
                                border: "1px solid #2E3350",
                                letterSpacing: ".04em",
                              }}
                            >
                              {ch}
                            </span>
                          ))}
                        </div>
                      )}
                      {pinInfo.length > 0 && (
                        <div className="text-xs mt-2" style={{ color: "#8A8FA8" }}>
                          {pinInfo.length} pinned across{" "}
                          {[...new Set(pinInfo.map((p) => cityEn(p.city)))].join(" · ")} — pins
                          never move.
                        </div>
                      )}
                    </div>
                    <div className="mt-3">
                      {cityP.grid.map((day, d) => (
                        <div key={d}>
                          <div
                            className="text-xs pt-2 pb-1"
                            style={{
                              color: "#6E7290",
                              borderTop: d ? "1px solid #1E2236" : "none",
                            }}
                          >
                            Day {d + 1}
                          </div>
                          {day.map((e, sl) => {
                            const locked = !!(e && e.locked);
                            const real = e && !e.locked ? e : null;
                            const isPin = real && pinned.has(real.id);
                            const isSrc = dnd && dnd.d === d && dnd.s === sl;
                            const isOver =
                              dnd && dnd.over && dnd.over.d === d && dnd.over.sl === sl && !isSrc;
                            return (
                              <div
                                key={sl}
                                ref={(el) => {
                                  slotRefs.current[`${d}-${sl}`] = el;
                                }}
                                onPointerDown={(ev) => chunkDown(ev, d, sl)}
                                className="w-full text-left rounded-2xl px-3.5 py-2.5 mb-1.5"
                                style={{
                                  cursor: real ? "grab" : "default",
                                  touchAction: real ? "none" : "auto",
                                  background: isOver
                                    ? dnd.valid
                                      ? "#E4572E14"
                                      : "#171A2B"
                                    : isPin
                                      ? "#E4572E10"
                                      : real
                                        ? "#171A2B"
                                        : "transparent",
                                  border: `1px ${real || isOver ? "solid" : "dashed"} ${isOver ? (dnd.valid ? "#E4572E" : "#5A5F78") : isPin ? "#E4572E" : "#2E3350"}`,
                                  opacity: isSrc ? 0.3 : 1,
                                  transition: "border-color .12s, background .12s, opacity .12s",
                                }}
                              >
                                <div className="flex justify-between items-baseline">
                                  <span
                                    className="text-xs"
                                    style={{
                                      color: isPin ? "#E4572E" : "#6E7290",
                                      letterSpacing: ".05em",
                                    }}
                                  >
                                    {SLOTS[sl]}
                                  </span>
                                  <span className="flex items-center gap-2">
                                    {isOver && !dnd.valid && (
                                      <span className="text-xs" style={{ color: "#5A5F78" }}>
                                        can't go here
                                      </span>
                                    )}
                                    {real && slotOf(real) !== 1 && (
                                      <span className="text-xs" style={{ color: "#6E7290" }}>
                                        {slotOf(real) === 0 ? "am only" : "evening only"}
                                      </span>
                                    )}
                                    {real && (
                                      <span
                                        className="text-xs"
                                        style={{ opacity: isPin ? 1 : 0.25 }}
                                      >
                                        📌
                                      </span>
                                    )}
                                  </span>
                                </div>
                                {real ? (
                                  <>
                                    <div className="flex items-baseline justify-between gap-2 mt-0.5">
                                      <span className="text-sm" style={{ color: "#F5F2E9" }}>
                                        {real.en}
                                      </span>
                                      {/* Tap here for detail. The cell itself is
                                          already taken: tap pins, drag moves. */}
                                      <span
                                        onPointerDown={(ev) => ev.stopPropagation()}
                                        onClick={(ev) => {
                                          ev.stopPropagation();
                                          setDetailExp(real);
                                        }}
                                        className="flex items-center justify-center rounded-full flex-shrink-0"
                                        style={{
                                          width: 22,
                                          height: 22,
                                          color: "#E4572E",
                                          fontSize: 14,
                                          border: "1px solid #3A3F63",
                                          cursor: "pointer",
                                        }}
                                        role="button"
                                        aria-label={`About ${real.en}`}
                                      >
                                        ›
                                      </span>
                                    </div>
                                    {real.hook && (
                                      <div
                                        className="text-xs mt-0.5"
                                        style={{ color: "#8A8FA8", lineHeight: 1.4 }}
                                      >
                                        {real.hook}
                                      </div>
                                    )}
                                  </>
                                ) : locked ? (
                                  <div
                                    className="text-xs mt-0.5"
                                    style={{ color: "#8A8FA8", lineHeight: 1.4 }}
                                  >
                                    🔒 A local-only spot — unlocks with your sidekick
                                  </div>
                                ) : (
                                  <div className="text-xs mt-0.5" style={{ color: "#5A5F78" }}>
                                    Free — {results.sidekick.name.split(" ")[0]}'s call, or drop a
                                    chunk here
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                    {dnd && dragged && (
                      <div
                        style={{
                          position: "fixed",
                          left: dnd.x + 10,
                          top: dnd.y + 10,
                          width: 210,
                          zIndex: 50,
                          pointerEvents: "none",
                          background: "#171A2B",
                          border: "1px solid #E4572E",
                          borderRadius: 14,
                          padding: "8px 12px",
                          boxShadow: "0 12px 32px rgba(0,0,0,.5)",
                        }}
                      >
                        <div className="text-sm" style={{ color: "#F5F2E9" }}>
                          {dragged.en}
                        </div>
                      </div>
                    )}
                    <button
                      onClick={reshuffle}
                      className="mt-2 w-full py-3 rounded-full text-sm font-semibold"
                      style={{
                        background: "#242842",
                        border: "1px solid #3A3F63",
                        color: "#F5F2E9",
                      }}
                    >
                      ✨ Shuffle everything unpinned
                      {pinInfo.length ? ` (keeps ${pinInfo.length})` : ""}
                    </button>
                    <button
                      onClick={() => setPhase("intro")}
                      className="mt-2 w-full py-3 rounded-full text-sm font-semibold"
                      style={{ background: "#E4572E", color: "#FFF7F0" }}
                    >
                      Perfect — done
                    </button>
                    <div className="text-xs mt-3" style={{ color: "#6E7290", lineHeight: 1.5 }}>
                      {results.sidekick.name.split(" ")[0]} reviews this draft with you on the intro
                      call — geography and bookings get locked then.
                    </div>
                  </div>
                );
              })()}

            {/* ACT 1 — the read */}
            {phase === "results" && results && (
              <div className="flex-1 overflow-y-auto px-5 pb-5 pt-2">
                <div className="fadeup">
                  <div className="text-xs" style={{ color: "#6E7290", letterSpacing: ".08em" }}>
                    {results.signals} signals read
                  </div>
                  <h2
                    style={{
                      color: "#F5F2E9",
                      fontFamily: "Georgia, serif",
                      fontSize: 28,
                      lineHeight: 1.1,
                      marginTop: 4,
                    }}
                  >
                    {results.arch.name}
                  </h2>
                  <p className="text-sm mt-1.5" style={{ color: "#8A8FA8", lineHeight: 1.55 }}>
                    <Hl text={results.arch.desc} />
                  </p>
                  <div className="flex gap-2 mt-2.5 flex-wrap">
                    <span
                      className="text-xs px-2.5 py-0.5 rounded-full"
                      style={{
                        color: "#E4572E",
                        border: "1px solid #E4572E55",
                        letterSpacing: ".06em",
                        fontWeight: 600,
                      }}
                    >
                      {results.travelType}
                    </span>
                    {results.contrastSeeker && (
                      <span
                        className="text-xs px-2.5 py-0.5 rounded-full"
                        style={{ color: "#8A8FA8", border: "1px solid #2E3350" }}
                      >
                        Contrast seeker
                      </span>
                    )}
                  </div>
                </div>
                <div
                  className="fadeup mx-auto"
                  style={{ animationDelay: ".1s", maxWidth: 212, marginTop: 2, marginBottom: 2 }}
                >
                  <Fingerprint axes={axes} uni={uni} />
                </div>
                <div>
                  {results.quips.map((q, i) => (
                    <div
                      key={i}
                      className="fadeup flex gap-3 py-3 text-sm"
                      style={{
                        animationDelay: `${0.2 + i * 0.12}s`,
                        color: "#CFCBBF",
                        lineHeight: 1.55,
                        textAlign: "left",
                        borderTop: i ? "1px solid #1E2236" : "1px solid #1E2236",
                      }}
                    >
                      <span
                        className="rounded-full flex-shrink-0"
                        style={{ width: 5, height: 5, background: "#E4572E", marginTop: 8 }}
                      />
                      <span>{q}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setPhase("match")}
                  className="mt-2 w-full py-3 rounded-full text-sm font-semibold"
                  style={{ background: "#E4572E", color: "#FFF7F0" }}
                >
                  So who's coming with me? →
                </button>
              </div>
            )}

            {/* ACT 2 — the match */}
            {phase === "match" && results && (
              <div className="flex-1 overflow-y-auto px-5 pb-5 pt-2">
                <div className="fadeup">
                  <div className="text-xs" style={{ color: "#6E7290", letterSpacing: ".08em" }}>
                    Out of every local we know…
                  </div>
                  <button
                    onClick={() => setPhase("profile")}
                    className="flex items-center justify-between mt-3 w-full text-left rounded-2xl px-3 py-3"
                    style={{ background: "#171A2B", border: "1px solid #2E3350" }}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div
                        className="flex items-center justify-center rounded-full flex-shrink-0"
                        style={{
                          width: 56,
                          height: 56,
                          background: "#12141F",
                          border: "1px solid #2E3350",
                          fontSize: 30,
                        }}
                      >
                        {results.sidekick.emoji}
                      </div>
                      <div className="min-w-0">
                        <div
                          className="truncate"
                          style={{
                            color: "#F5F2E9",
                            fontFamily: "Georgia, serif",
                            fontSize: 24,
                            lineHeight: 1.1,
                          }}
                        >
                          {results.sidekick.name}
                        </div>
                        <div className="text-xs mt-1 truncate" style={{ color: "#8A8FA8" }}>
                          {results.sidekick.base} · {results.sidekick.abroad}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 pl-2">
                      <div
                        style={{
                          color: "#E4572E",
                          fontFamily: "Georgia, serif",
                          fontSize: 30,
                          lineHeight: 1,
                        }}
                      >
                        {results.matchPct}%
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: "#6E7290" }}>
                        match
                      </div>
                    </div>
                  </button>
                  <p className="text-sm mt-3" style={{ color: "#8A8FA8", lineHeight: 1.55 }}>
                    {results.sidekick.line}
                  </p>
                  <div className="text-xs mt-1.5" style={{ color: "#6E7290" }}>
                    {results.sidekick.type} · shares {results.shared}/4 letters with you
                  </div>
                  <button
                    onClick={() => setPhase("profile")}
                    className="mt-3 w-full py-2.5 rounded-full text-sm font-semibold"
                    style={{
                      background: "rgba(228,87,46,.12)",
                      border: "1px solid #E4572E",
                      color: "#E4572E",
                    }}
                  >
                    {results.sidekick.emoji} Meet {results.sidekick.name.split(" ")[0]} — full
                    profile →
                  </button>
                </div>
                <div className="mt-3">
                  {results.reasons.map((r, i) => (
                    <div
                      key={i}
                      className="fadeup flex gap-3 py-3 text-sm"
                      style={{
                        animationDelay: `${0.15 + i * 0.12}s`,
                        color: "#CFCBBF",
                        lineHeight: 1.55,
                        textAlign: "left",
                        borderTop: "1px solid #1E2236",
                      }}
                    >
                      <span
                        className="rounded-full flex-shrink-0"
                        style={{ width: 5, height: 5, background: "#E4572E", marginTop: 8 }}
                      />
                      <span>{r}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setPhase("plan")}
                  className="mt-2 w-full py-3 rounded-full text-sm font-semibold"
                  style={{ background: "#E4572E", color: "#FFF7F0" }}
                >
                  Show us the trip →
                </button>
                <button
                  onClick={() => setPhase("results")}
                  className="mt-1 py-2 text-xs"
                  style={{ color: "#6E7290" }}
                >
                  ← Back to my read
                </button>
              </div>
            )}

            {/* PROFILE — the sidekick is the product, not the itinerary */}
            {phase === "profile" &&
              results &&
              (() => {
                const sk = results.sidekick;
                const first = sk.name.split(" ")[0];
                const TILES = [
                  { cap: "Portrait", g: ["#242842", "#171A2B"] },
                  { cap: "Guiding in action", g: ["#2E3350", "#1B1E30"] },
                  { cap: "Home turf", g: ["#3A2A2E", "#191A28"] },
                  { cap: "Off duty", g: ["#1F2A3A", "#141726"] },
                ];
                const BARS = [6, 13, 21, 14, 27, 18, 30, 23, 12, 25, 16, 28, 10, 21, 14, 8, 18, 11];
                return (
                  <div className="flex-1 overflow-y-auto px-5 pb-5 pt-2 text-left">
                    <button
                      onClick={() => setPhase("match")}
                      className="text-xs"
                      style={{ color: "#6E7290" }}
                    >
                      ← Back
                    </button>

                    <div
                      className="fadeup noscroll mt-2 -mx-5 px-5 flex gap-2 overflow-x-auto"
                      style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none" }}
                      onScroll={(ev) =>
                        setPhotoIdx(
                          Math.min(
                            TILES.length - 1,
                            Math.max(0, Math.round(ev.currentTarget.scrollLeft / 158)),
                          ),
                        )
                      }
                    >
                      {TILES.map((t) => (
                        <div
                          key={t.cap}
                          className="relative rounded-2xl flex-shrink-0 overflow-hidden"
                          style={{
                            width: 150,
                            height: 200,
                            scrollSnapAlign: "start",
                            background: `linear-gradient(160deg, ${t.g[0]}, ${t.g[1]})`,
                            border: "1px solid #2E3350",
                          }}
                        >
                          <div
                            className="absolute inset-0 flex items-center justify-center"
                            style={{ fontSize: 62, opacity: 0.92 }}
                          >
                            {sk.emoji}
                          </div>
                          <div
                            className="absolute left-0 right-0 bottom-0 px-3 pt-6 pb-2"
                            style={{
                              background:
                                "linear-gradient(to top, rgba(10,11,20,.92), rgba(10,11,20,0))",
                              fontSize: 10,
                              color: "#CFCBBF",
                              letterSpacing: ".08em",
                            }}
                          >
                            {t.cap}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-center gap-1.5 mt-2">
                      {TILES.map((t, i) => (
                        <span
                          key={t.cap}
                          className="rounded-full"
                          style={{
                            width: 5,
                            height: 5,
                            background: i === photoIdx ? "#E4572E" : "#2E3350",
                            transition: "background .2s",
                          }}
                        />
                      ))}
                    </div>

                    <div className="flex items-start justify-between mt-4">
                      <div className="min-w-0">
                        <div
                          style={{
                            color: "#F5F2E9",
                            fontFamily: "Georgia, serif",
                            fontSize: 28,
                            lineHeight: 1.1,
                          }}
                        >
                          {sk.name}
                        </div>
                        <div className="text-xs mt-1" style={{ color: "#8A8FA8" }}>
                          {sk.base}
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: "#6E7290" }}>
                          {sk.abroad}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 pl-3">
                        <div
                          style={{
                            color: "#E4572E",
                            fontFamily: "Georgia, serif",
                            fontSize: 28,
                            lineHeight: 1,
                          }}
                        >
                          {results.matchPct}%
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: "#6E7290" }}>
                          match
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-1.5 mt-3 flex-wrap">
                      {[
                        "✓ ID verified",
                        "✓ Background check",
                        sk.trust.langs,
                        `${sk.trust.trips} trips led`,
                      ].map((ch) => (
                        <span
                          key={ch}
                          className="px-2 py-0.5 rounded-full"
                          style={{
                            fontSize: 10,
                            color: "#8A8FA8",
                            border: "1px solid #2E3350",
                            letterSpacing: ".04em",
                          }}
                        >
                          {ch}
                        </span>
                      ))}
                    </div>

                    <div className="mt-4 pt-3" style={{ borderTop: "1px solid #1E2236" }}>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setVoicePlaying((v) => !v)}
                          className="flex items-center justify-center rounded-full flex-shrink-0"
                          style={{
                            width: 36,
                            height: 36,
                            background: "#E4572E",
                            color: "#FFF7F0",
                            fontSize: 12,
                          }}
                        >
                          {voicePlaying ? "❚❚" : "▶"}
                        </button>
                        <div className="flex items-center gap-0.5 flex-1" style={{ height: 30 }}>
                          {BARS.map((h, i) => (
                            <span
                              key={i}
                              className="rounded-full flex-1"
                              style={{ height: h, background: i % 3 === 0 ? "#E4572E" : "#2E3350" }}
                            />
                          ))}
                        </div>
                        <span
                          className="text-xs flex-shrink-0"
                          style={{ color: "#6E7290", fontVariantNumeric: "tabular-nums" }}
                        >
                          0:15
                        </span>
                      </div>
                      <div
                        className="mt-2"
                        style={{
                          fontSize: 12,
                          fontStyle: "italic",
                          color: "#8A8FA8",
                          lineHeight: 1.5,
                        }}
                      >
                        “{sk.voice}”
                      </div>
                    </div>

                    <div className="mt-3">
                      {sk.prompts.map((pr) => (
                        <div key={pr.q} className="py-3" style={{ borderTop: "1px solid #1E2236" }}>
                          <div
                            style={{
                              fontSize: 11,
                              textTransform: "uppercase",
                              letterSpacing: ".1em",
                              color: "#6E7290",
                            }}
                          >
                            {pr.q}
                          </div>
                          <div
                            className="mt-1"
                            style={{
                              fontSize: 16,
                              fontFamily: "Georgia, serif",
                              color: "#F5F2E9",
                              lineHeight: 1.4,
                            }}
                          >
                            {pr.a}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="pt-3" style={{ borderTop: "1px solid #1E2236" }}>
                      <div className="text-xs" style={{ color: "#6E7290", letterSpacing: ".08em" }}>
                        Why you two
                      </div>
                      {results.reasons.map((r, i) => (
                        <div
                          key={i}
                          className="flex gap-3 py-2.5 text-sm"
                          style={{ color: "#CFCBBF", lineHeight: 1.55 }}
                        >
                          <span
                            className="rounded-full flex-shrink-0"
                            style={{ width: 5, height: 5, background: "#E4572E", marginTop: 8 }}
                          />
                          <span>{r}</span>
                        </div>
                      ))}
                    </div>

                    <div
                      className="pt-3"
                      style={{
                        borderTop: "1px solid #1E2236",
                        fontSize: 13,
                        fontStyle: "italic",
                        color: "#CFCBBF",
                        lineHeight: 1.5,
                      }}
                    >
                      {sk.trust.review}
                    </div>

                    <button
                      onClick={() => {
                        setBookingFrom("profile");
                        setPhase("booking");
                      }}
                      className="mt-4 w-full py-3 rounded-full text-sm font-semibold"
                      style={{ background: "#E4572E", color: "#FFF7F0" }}
                    >
                      Book {first} for this trip →
                    </button>
                    <button
                      onClick={() => setPhase("plan")}
                      className="mt-2 w-full py-3 rounded-full text-sm font-semibold"
                      style={{
                        background: "#242842",
                        border: "1px solid #3A3F63",
                        color: "#F5F2E9",
                      }}
                    >
                      Show us the trip →
                    </button>
                  </div>
                );
              })()}

            {/* ACT 3 — the plan, as an information timeline */}
            {phase === "plan" &&
              results &&
              (() => {
                const plan = results.cityPlan;
                const legs = plan.slice(0, -1).map((cp, i) => legFor(cp.city, plan[i + 1].city));
                const hops = legs.filter((l) => l[1]);
                const easiest = hops.length ? hops.reduce((a, b) => (a[1] < b[1] ? a : b)) : null;
                const easyIdx = easiest ? legs.indexOf(easiest) : -1;
                return (
                  <div className="flex-1 overflow-y-auto px-5 pb-5 pt-2">
                    <div className="fadeup">
                      <div className="text-xs" style={{ color: "#6E7290", letterSpacing: ".08em" }}>
                        Your {tripDays} days in {cityEn(results.route.cities[0])}, drawn out
                      </div>
                      <h2
                        style={{
                          color: "#F5F2E9",
                          fontFamily: "Georgia, serif",
                          fontSize: 28,
                          lineHeight: 1.1,
                          marginTop: 4,
                        }}
                      >
                        {results.route.name}
                      </h2>
                      {easyIdx >= 0 && (
                        <p
                          className="text-sm mt-1.5"
                          style={{ color: "#8A8FA8", lineHeight: 1.55 }}
                        >
                          {cityEn(plan[easyIdx].city)} and {cityEn(plan[easyIdx + 1].city)} are only
                          a{" "}
                          <span style={{ color: "#F5F2E9" }}>{fmtLeg(easiest).toLowerCase()}</span>{" "}
                          apart — do them back to back.
                        </p>
                      )}
                    </div>
                    <div className="mt-4">
                      {plan.map((cp, i) => (
                        <div
                          key={cp.city}
                          className="fadeup flex gap-3"
                          style={{ animationDelay: `${0.1 + i * 0.1}s` }}
                        >
                          <div className="flex flex-col items-center" style={{ width: 10 }}>
                            <span
                              className="rounded-full flex-shrink-0"
                              style={{ width: 8, height: 8, background: "#E4572E", marginTop: 6 }}
                            />
                            {i < plan.length - 1 && (
                              <span
                                style={{
                                  width: 1,
                                  flex: 1,
                                  background: "#2E3350",
                                  margin: "4px 0",
                                }}
                              />
                            )}
                          </div>
                          <div
                            className="flex-1"
                            style={{ paddingBottom: i < plan.length - 1 ? 4 : 0 }}
                          >
                            <div className="flex justify-between items-baseline">
                              <span style={{ color: "#F5F2E9", fontSize: 15 }}>
                                {cityEn(cp.city)}{" "}
                                <span style={{ color: "#6E7290", fontSize: 12 }}>{cp.city}</span>
                              </span>
                              <span className="text-xs" style={{ color: "#6E7290" }}>
                                {results.per} days
                              </span>
                            </div>
                            {CITY_TAG[cp.city] && (
                              <div
                                className="mt-0.5"
                                style={{
                                  fontFamily: "Georgia, serif",
                                  fontStyle: "italic",
                                  fontSize: 14,
                                  color: "#CFCBBF",
                                  lineHeight: 1.45,
                                }}
                              >
                                “{CITY_TAG[cp.city]}”
                              </div>
                            )}
                            {CITY_INTRO[cp.city] && (
                              <div
                                className="text-xs mt-1"
                                style={{ color: "#8A8FA8", lineHeight: 1.6 }}
                              >
                                {CITY_INTRO[cp.city]}
                              </div>
                            )}
                            {cityChips(cp.city).length > 0 && (
                              <div className="flex gap-1.5 mt-1.5 flex-wrap">
                                {cityChips(cp.city).map((ch) => (
                                  <span
                                    key={ch}
                                    className="px-2 py-0.5 rounded-full"
                                    style={{
                                      fontSize: 10,
                                      color: "#8A8FA8",
                                      border: "1px solid #2E3350",
                                      letterSpacing: ".04em",
                                    }}
                                  >
                                    {ch}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="mt-1.5">
                              {cp.exps.map((e) => (
                                <button
                                  key={e.id}
                                  onClick={() => setDetailExp(e)}
                                  className="w-full flex items-center justify-between gap-2 rounded-xl px-3 py-2 mb-1"
                                  style={{
                                    background: "#171A2B",
                                    border: "1px solid #2E3350",
                                    textAlign: "left",
                                  }}
                                >
                                  <span
                                    style={{ fontSize: 12.5, color: "#CFCBBF", lineHeight: 1.35 }}
                                  >
                                    {e.en}
                                    <span style={{ color: "#6E7290" }}> · {e.cn}</span>
                                  </span>
                                  <span style={{ color: "#E4572E", fontSize: 15, flexShrink: 0 }}>
                                    ›
                                  </span>
                                </button>
                              ))}
                            </div>
                            {cp.exps[0]?.hook && (
                              <div
                                className="text-xs mt-0.5"
                                style={{ color: "#6E7290", lineHeight: 1.5 }}
                              >
                                {cp.exps[0].hook}
                              </div>
                            )}
                            {/* Before you go — the reality check, bilingual.
                                Collapsed by default so the itinerary stays readable. */}
                            {(() => {
                              const flagged = cp.exps.filter((e) => hasFullGuide(GUIDES[e.id]));
                              if (!flagged.length) return null;
                              const open = openGuide === cp.city;
                              return (
                                <div className="mt-2">
                                  <button
                                    onClick={() => setOpenGuide(open ? null : cp.city)}
                                    className="w-full flex items-center justify-between rounded-xl px-3 py-2"
                                    style={{
                                      background: "rgba(228,87,46,.10)",
                                      border: "1px solid rgba(228,87,46,.35)",
                                      color: "#FFD9CC",
                                      fontSize: 11.5,
                                      letterSpacing: ".03em",
                                    }}
                                  >
                                    <span>⚠️ Before you go · 出发前须知 ({flagged.length})</span>
                                    <span style={{ opacity: 0.7 }}>{open ? "▾" : "▸"}</span>
                                  </button>
                                  {open && (
                                    <div
                                      className="mt-1.5 rounded-xl px-3 py-2.5"
                                      style={{ background: "#12141F", border: "1px solid #2E3350" }}
                                    >
                                      {flagged.map((e, gi) => {
                                        const g = GUIDES[e.id]!;
                                        return (
                                          <div
                                            key={e.id}
                                            style={{
                                              paddingTop: gi ? 10 : 0,
                                              marginTop: gi ? 10 : 0,
                                              borderTop: gi ? "1px solid #1E2236" : undefined,
                                            }}
                                          >
                                            <div style={{ fontSize: 12.5, color: "#F5F2E9" }}>
                                              {e.en}
                                              <span style={{ color: "#6E7290" }}> · {e.cn}</span>
                                            </div>
                                            {g.altitude && g.altitude >= 2500 && (
                                              <div
                                                className="mt-1"
                                                style={{ fontSize: 11.5, color: "#FFD9CC" }}
                                              >
                                                🫁 {g.altitude}m — altitude sickness is possible ·
                                                海拔 {g.altitude} 米，可能有高原反应
                                              </div>
                                            )}
                                            {g.minAge && (
                                              <div
                                                className="mt-0.5"
                                                style={{ fontSize: 11.5, color: "#FFD9CC" }}
                                              >
                                                👶 Suggested age {g.minAge}+ · 建议 {g.minAge}{" "}
                                                岁以上
                                              </div>
                                            )}
                                            {g.realityEn && (
                                              <div
                                                className="mt-1.5"
                                                style={{
                                                  fontSize: 11.5,
                                                  color: "#CFCBBF",
                                                  lineHeight: 1.6,
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    color: "#8A8FA8",
                                                    fontSize: 10,
                                                    letterSpacing: ".08em",
                                                    textTransform: "uppercase",
                                                  }}
                                                >
                                                  What the photos don't say
                                                </div>
                                                <div className="mt-0.5">{g.realityEn}</div>
                                                <div style={{ marginTop: 3, color: "#B8B5AC" }}>
                                                  {g.realityCn}
                                                </div>
                                              </div>
                                            )}
                                            {g.tipsEn?.length ? (
                                              <div
                                                className="mt-1.5"
                                                style={{ fontSize: 11.5, lineHeight: 1.6 }}
                                              >
                                                {g.tipsEn.map((t, ti) => (
                                                  <div key={t} className="flex gap-2 mt-0.5">
                                                    <span style={{ color: "#E4572E" }}>·</span>
                                                    <span>
                                                      <span style={{ color: "#CFCBBF" }}>{t}</span>
                                                      <span style={{ color: "#8A8FA8" }}>
                                                        {" "}
                                                        / {g.tipsCn?.[ti]}
                                                      </span>
                                                    </span>
                                                  </div>
                                                ))}
                                              </div>
                                            ) : null}
                                            {g.seasonEn && (
                                              <div
                                                className="mt-1.5"
                                                style={{
                                                  fontSize: 11.5,
                                                  color: "#E0B14F",
                                                  lineHeight: 1.6,
                                                }}
                                              >
                                                📅 {g.seasonEn}
                                                <span style={{ color: "#8A8FA8" }}>
                                                  {" "}
                                                  / {g.seasonCn}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            <div
                              className="mt-1"
                              style={{
                                fontSize: 12,
                                fontStyle: "italic",
                                color: "#E4572E",
                                opacity: 0.85,
                                lineHeight: 1.5,
                              }}
                            >
                              🔑 What {results.sidekick.name.split(" ")[0]} adds here:{" "}
                              {results.sidekick.adds[cp.city] ||
                                results.sidekick.fallbackAdds.replace("{city}", cityEn(cp.city))}
                            </div>
                            {i < plan.length - 1 && (
                              <div
                                className="text-xs"
                                style={{ color: "#8A8FA8", margin: "10px 0 12px" }}
                              >
                                ↓ {fmtLeg(legs[i])}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {/* What we removed and why. Filtering silently would be
                          just another way of hiding the truth from someone
                          who asked to see the pitfalls. */}
                      {results.excluded.length > 0 && (
                        <div
                          className="mt-2 rounded-xl px-3 py-2.5"
                          style={{ background: "#12141F", border: "1px solid #2E3350" }}
                        >
                          <div
                            style={{
                              fontSize: 10,
                              letterSpacing: ".08em",
                              textTransform: "uppercase",
                              color: "#8A8FA8",
                            }}
                          >
                            Left out for your party · 已为你排除
                          </div>
                          {results.excluded.map((x) => (
                            <div
                              key={x.id}
                              className="mt-1.5"
                              style={{ fontSize: 11.5, lineHeight: 1.55 }}
                            >
                              <span style={{ color: "#CFCBBF" }}>{x.en}</span>
                              <span style={{ color: "#6E7290" }}> · {x.cn}</span>
                              <div style={{ color: "#E0B14F" }}>
                                {x.reasonEn}
                                <span style={{ color: "#8A8FA8" }}> / {x.reasonCn}</span>
                              </div>
                            </div>
                          ))}
                          <div
                            className="mt-2"
                            style={{ fontSize: 10, color: "#6E7290", lineHeight: 1.5 }}
                          >
                            Altitude guidance is a planning default, not medical advice — ask a
                            doctor. · 海拔限制是行程默认设置，不构成医疗建议，请咨询医生。
                          </div>
                        </div>
                      )}
                      {results.zoneNote && (
                        <div
                          className="text-xs py-2.5 mt-1"
                          style={{ color: "#E4572E", borderTop: "1px solid #1E2236" }}
                        >
                          ⚠ This route crosses a separate entry zone (HK / Taiwan / Tibet) — check
                          permits & visas.
                        </div>
                      )}
                    </div>
                    <button
                      className="mt-3 w-full py-3 rounded-full text-sm font-semibold"
                      style={{ background: "#E4572E", color: "#FFF7F0" }}
                      onClick={() => {
                        setBookingFrom("plan");
                        setPhase("booking");
                      }}
                    >
                      Book {results.sidekick.name.split(" ")[0]} for this trip →
                    </button>
                    <button
                      onClick={() => setPhase("intro")}
                      className="mt-1 py-2 text-xs"
                      style={{ color: "#6E7290" }}
                    >
                      Swipe again ↺
                    </button>
                  </div>
                );
              })()}

            {/* Detail sheet over whatever screen you were on. */}
            {detailExp && (
              <ExperienceDetail
                exp={detailExp}
                onClose={() => setDetailExp(null)}
                sidekickNote={
                  results
                    ? `What ${results.sidekick.name.split(" ")[0]} adds here: ${
                        results.sidekick.adds[detailExp.city] ||
                        results.sidekick.fallbackAdds.replace("{city}", cityEn(detailExp.city))
                      }`
                    : undefined
                }
              />
            )}
          </div>
        </div>

        {/* ALGORITHM PANEL */}
        <div
          className="w-full lg:w-80 rounded-2xl p-5 self-stretch"
          style={{ background: "#171A2Bcc", border: "1px solid #2E3350" }}
        >
          <div
            className="text-xs uppercase tracking-widest mb-1"
            style={{ color: "#8A8FA8", letterSpacing: ".2em" }}
          >
            What Sidekick knows
          </div>
          <div className="text-xs mb-4" style={{ color: "#6E7290" }}>
            Pool: {pool.length} experiences after constraints. Dot = lean, glow = care; bars =
            intensities.
          </div>
          <div className="space-y-3">
            {AXES.map((axis) => {
              const { lean, care } = axes[axis.key];
              return (
                <div key={axis.key}>
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: "#B8B5AC" }}>{axis.labels[0]}</span>
                    <span style={{ color: "#6E7290" }}>care {Math.round(care * 100)}</span>
                    <span style={{ color: "#B8B5AC" }}>{axis.labels[1]}</span>
                  </div>
                  <div className="relative h-2 rounded-full" style={{ background: "#242842" }}>
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "#F5F2E9",
                        opacity: 0.05 + care * 0.18,
                        transition: "opacity .4s ease",
                      }}
                    />
                    <div
                      className="absolute top-0 bottom-0"
                      style={{ left: "50%", width: 1, background: "#0D0F1C" }}
                    />
                    <div
                      className="absolute rounded-full"
                      style={{
                        left: `calc(${((lean + 1) / 2) * 100}% - 6px)`,
                        top: -2,
                        width: 12,
                        height: 12,
                        background: care > 0.05 ? "#E4572E" : "#3A3F63",
                        border: "2px solid #12141F",
                        transition: "left .4s ease, background .4s ease",
                      }}
                    />
                  </div>
                </div>
              );
            })}
            <div className="pt-1" />
            {UNI.map((d) => (
              <div key={d.key}>
                <div className="flex justify-between text-xs mb-1">
                  <span style={{ color: "#B8B5AC" }}>{d.label}</span>
                  <span style={{ color: "#B8B5AC", fontVariantNumeric: "tabular-nums" }}>
                    {Math.round(uni[d.key] * 100)}
                  </span>
                </div>
                <div className="h-1.5 rounded-full" style={{ background: "#242842" }}>
                  <div
                    className="h-1.5 rounded-full"
                    style={{
                      width: `${uni[d.key] * 100}%`,
                      background: "#B8B5AC",
                      transition: "width .4s ease",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div
            className="mt-4 pt-3 text-xs space-y-1"
            style={{ borderTop: "1px solid #2E3350", color: "#6E7290" }}
          >
            <div>
              ♥ <span style={{ color: "#B8B5AC" }}>{likes.length}</span> · ✕{" "}
              <span style={{ color: "#B8B5AC" }}>{dislikes.length}</span> · month {tripMonth}
              {kids ? " · kids mode" : ""}
            </div>
            {phase === "swipe" && (
              <div style={{ color: "#B8B5AC" }}>Arrow keys work too · ↑ takes one back.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
